import { sortRows, type IdentifyModel } from '@repo/front-end-utils';
import type { BulkUploadTwinAssetItem, TwinAssetResponse } from '@repo/front-end-services';
import { equipmentSortAccessors } from './equipmentRegisterColumns';
import type { EquipmentSort } from './EquipmentRowsView';

/**
 * The register rows in the order the open view shows them (`equipment-page-bulk-actions`, R2):
 * *Rename as a Set* numbers in this order and *Change Product* seeds its picker from the first.
 *
 * It makes the SAME calls each view makes, so the two cannot disagree:
 * - Rows view: `EquipmentRowsView`'s `shown` — the visible rows through `sortRows` with
 *   `equipmentSortAccessors`, the register's own order when unsorted.
 * - Groups view: `EquipmentGroupsView`'s `shown` — `model.groups` in order, each group's rows in
 *   order, filtered to the visible ones.
 * The selection's tick order (`useEquipmentSelection`) is never the shown order.
 *
 * *From this import* (`equipment-page-reimport`, R11) overrides the Rows view's order in both:
 * `fromImportOrder`, below, is the one function each calls.
 */

/**
 * *From this import* (R11): the rows an upload that updated or left out rows wrote, added first.
 * Page state, held in session only, from the moment the upload lands until the operator picks a
 * chip, a view, a sort or a search, undoes, or the twin changes.
 */
export interface FromImport {
  /** The record the upload wrote; null from a middleware that predates the record. */
  importId: number | null;
  /** The written rows' ids: added first, then updated, each in the upload answer's order. */
  order: readonly number[];
  /** The ids the upload added — the rows that glow. */
  added: ReadonlySet<number>;
}

/**
 * The override from an upload's answer. An item with no `outcome` (a middleware that predates the
 * field; the current one sets it on every item) counts as UPDATED, so nothing is shown as added
 * that may not have been.
 */
export const fromImportOf = (
  importId: number | null,
  items: readonly BulkUploadTwinAssetItem[],
): FromImport => {
  const added: number[] = [];
  const updated: number[] = [];
  const seen = new Set<number>();
  items.forEach(item => {
    const id = item.asset?.id;
    if (typeof id !== 'number' || seen.has(id)) return;
    seen.add(id);
    (item.outcome === 'created' ? added : updated).push(id);
  });
  return { importId, order: [...added, ...updated], added: new Set(added) };
};

/**
 * The import's rows still on the register and allowed by the view's filter, in the import's
 * order — the sort is skipped while the override stands.
 */
export const fromImportOrder = (
  rows: readonly TwinAssetResponse[],
  visibleIds: ReadonlySet<number> | undefined,
  fromImport: FromImport,
): number[] => {
  const onRegister = new Set(rows.map(row => row.id));
  return fromImport.order.filter(
    id => onRegister.has(id) && (visibleIds === undefined || visibleIds.has(id)),
  );
};

/** As `EquipmentRowsView`'s own (unexported) stand-in: no state, no plan. */
const UNCOUNTED_MODEL: Pick<IdentifyModel, 'stateOf' | 'planStateOf'> = {
  stateOf: () => undefined,
  planStateOf: () => undefined,
};

export interface ShownOrderInput {
  view: 'rows' | 'groups';
  rows: readonly TwinAssetResponse[];
  /** The ids the view is given — `undefined` when it shows every row. */
  visibleIds: ReadonlySet<number> | undefined;
  sort: EquipmentSort;
  /** The model the view is given (`null` where the Rows view reads rows uncounted). */
  model: IdentifyModel | null;
  /** *From this import*, while it stands (Rows view only). */
  fromImport?: FromImport | null;
}

export const equipmentShownOrder = ({
  view,
  rows,
  visibleIds,
  sort,
  model,
  fromImport,
}: ShownOrderInput): number[] => {
  if (view === 'groups' && model) {
    return model.groups.flatMap(group =>
      visibleIds ? group.rowIds.filter(id => visibleIds.has(id)) : group.rowIds,
    );
  }
  if (fromImport) return fromImportOrder(rows, visibleIds, fromImport);
  const visible = visibleIds ? rows.filter(row => visibleIds.has(row.id)) : [...rows];
  return sortRows(
    visible,
    sort.field,
    sort.direction,
    equipmentSortAccessors(model ?? UNCOUNTED_MODEL),
  ).map(row => row.id);
};

/** The selected rows, in shown order. A selected id the view does not show is left out. */
export const selectionInShownOrder = (
  selected: readonly number[],
  shown: readonly number[],
  rows: readonly TwinAssetResponse[],
): TwinAssetResponse[] => {
  if (selected.length === 0) return [];
  const chosen = new Set(selected);
  const byId = new Map(rows.map(row => [row.id, row]));
  return shown
    .filter(id => chosen.has(id))
    .map(id => byId.get(id))
    .filter((row): row is TwinAssetResponse => row !== undefined);
};
